import React from "react";

const variantBase = {
  default:
    "inline-flex min-h-[36px] items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-extrabold shadow-[0_2px_0_#D0D0D0] transition active:translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm",
  primary:
    "inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl px-8 text-sm font-extrabold uppercase tracking-wide text-white transition-transform shadow-[0_var(--slab-lift-y)_0_var(--slab-lip)] active:translate-y-1 active:shadow-[0_var(--slab-lift-y-active)_0_var(--slab-lip)] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0",
  /** Light fill + colored border + “lip” shadow (e.g. save-to-device / secondary CTA). */
  softOutline:
    "inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#1CB0F6] bg-[#E8F6FE] px-6 text-base font-extrabold uppercase tracking-wide text-[#1899D6] shadow-[0_4px_0_#7CC4F0] transition-transform active:translate-y-1 active:shadow-[0_1px_0_#7CC4F0] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0 sm:w-auto",
} as const;

const defaultSurfaceClass = "bg-[#F4F4F5] text-[#3C3C3C]";

const PRIMARY_DEFAULT_BG = "#14B8A6";
const PRIMARY_DEFAULT_LIP = "#0F766E";

function lipForPrimary(
  backgroundColor: string | undefined,
  implicitBackground: string | undefined,
  lipColor: string | undefined,
): string {
  if (lipColor) return lipColor;
  const bg = backgroundColor ?? implicitBackground ?? PRIMARY_DEFAULT_BG;
  if (bg === PRIMARY_DEFAULT_BG) return PRIMARY_DEFAULT_LIP;
  return `color-mix(in srgb, ${bg} 62%, #000)`;
}

export type SlabButtonVariant = keyof typeof variantBase;

export type SlabButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "style"
> & {
  /** `"default"` — compact slab; `"primary"` — tall filled CTA; `"softOutline"` — bordered light CTA. */
  variant?: SlabButtonVariant;
  /** When set, overrides default surface background (Tailwind `bg-*` is skipped). For `variant="primary"`, defaults to teal if omitted. */
  backgroundColor?: string;
  /** When set, background switches to this color while hovered (requires `backgroundColor` or inline bg for a sensible rest state). */
  hoverColor?: string;
  /**
   * Dark “lip” under the primary slab. Defaults to `#0F766E` for the default teal primary,
   * otherwise a `color-mix` darkening of `backgroundColor`.
   */
  lipColor?: string;
  /** Vertical slab depth (rest state) for `variant="primary"`. */
  shadowLiftPx?: number;
  /** Vertical slab depth while pressed for `variant="primary"`. */
  shadowLiftActivePx?: number;
  style?: React.CSSProperties;
};

export const SlabButton = React.forwardRef<HTMLButtonElement, SlabButtonProps>(
  (
    {
      className,
      variant = "default",
      backgroundColor,
      hoverColor,
      lipColor,
      shadowLiftPx = 8,
      shadowLiftActivePx = 3,
      style,
      onMouseEnter,
      onMouseLeave,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    const [hovered, setHovered] = React.useState(false);

    const implicitBackground =
      backgroundColor ??
      (variant === "primary" ? PRIMARY_DEFAULT_BG : undefined);

    const resolvedBackground =
      hovered && hoverColor
        ? hoverColor
        : implicitBackground !== undefined
          ? implicitBackground
          : undefined;

    const useDefaultSurface =
      variant === "default" &&
      backgroundColor === undefined &&
      resolvedBackground === undefined;

    const primaryLip =
      variant === "primary"
        ? lipForPrimary(backgroundColor, implicitBackground, lipColor)
        : undefined;

    return (
      <button
        ref={ref}
        type={type}
        className={[
          variantBase[variant],
          useDefaultSurface ? defaultSurfaceClass : null,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          ...(variant === "primary" && {
            ["--slab-lip" as string]: primaryLip,
            ["--slab-lift-y" as string]: `${shadowLiftPx}px`,
            ["--slab-lift-y-active" as string]: `${shadowLiftActivePx}px`,
          }),
          ...(resolvedBackground !== undefined && {
            backgroundColor: resolvedBackground,
          }),
          ...style,
        }}
        onMouseEnter={(e) => {
          setHovered(true);
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          setHovered(false);
          onMouseLeave?.(e);
        }}
        {...rest}
      />
    );
  },
);
SlabButton.displayName = "SlabButton";

export default SlabButton;
